import {useCallback, useMemo, useRef, useState} from 'react';
import {searchCrGovByCompanyName, CrGovCompanyHit} from '@/services/accountService/companyService';

/** Form fields compared / written from CR */
export type CompanyCrFormSlice = {
    companyName?: string;
    companyChineseName?: string;
    inCorpDate?: string;
    br?: string;
};

/** Validate outcome for stepper */
export type CompanyCrValidateOutcome = 'matched' | 'opened' | 'empty' | 'error';

/** Parent-triggered Validate handle */
export type CompanyCrValidateHandle = {
    validate: (anchorEl: HTMLElement | null) => Promise<CompanyCrValidateOutcome>;
};

/** Show in-popover fuzzy filter only when raw hits exceed this count */
export const CR_GOV_FILTER_MIN_HITS = 5;

const EMPTY_MSG = 'Not have any record from GOV, please confirm!';

function norm(v?: string | null): string {
    return (v ?? '').trim();
}

/** Whether form four fields equal one CR hit */
export function isSameAsCrHit(form: CompanyCrFormSlice, hit: CrGovCompanyHit): boolean {
    return (
        norm(form.companyName).toUpperCase() === norm(hit.companyName).toUpperCase()
        && norm(form.companyChineseName) === norm(hit.companyChineseName)
        && norm(form.inCorpDate) === norm(hit.incorporationDate)
        && norm(form.br) === norm(hit.brNo)
    );
}

/** Whether any hit matches form four fields */
export function hasMatchingCrHit(form: CompanyCrFormSlice, hits: CrGovCompanyHit[]): boolean {
    return hits.some((h) => isSameAsCrHit(form, h));
}

/** Case-insensitive substring match across CR display fields */
export function matchCrHitFuzzy(hit: CrGovCompanyHit, query: string): boolean {
    const q = norm(query).toLowerCase();
    if (!q) return true;
    const hay = [
        hit.companyName,
        hit.companyChineseName,
        hit.incorporationDate,
        hit.brNo,
        hit.source,
    ].map((v) => norm(v).toLowerCase()).join(' ');
    return hay.includes(q);
}

type SnackSeverity = 'success' | 'error' | 'warning' | 'info';

type Options = {
    getForm: () => CompanyCrFormSlice;
    applyHit: (hit: CrGovCompanyHit) => void;
    showSnackbar: (message: string, severity?: SnackSeverity) => void;
};

/**
 * CompanyName CR GOV lookup / Validate / Convert (form only, no persist).
 */
export function useCompanyCrGovLookup(options: Options) {
    const optsRef = useRef(options);
    optsRef.current = options;

    const [lookingUp, setLookingUp] = useState(false);
    const lookingUpRef = useRef(false);
    const [hits, setHits] = useState<CrGovCompanyHit[]>([]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [filterQuery, setFilterQuery] = useState('');
    const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);

    const filteredHits = useMemo(() => {
        if (hits.length <= CR_GOV_FILTER_MIN_HITS) return hits;
        return hits.filter((h) => matchCrHitFuzzy(h, filterQuery));
    }, [hits, filterQuery]);

    /** Filter input visible only when raw list has more than 5 rows */
    const showFilter = hits.length > CR_GOV_FILTER_MIN_HITS;

    const closePopover = useCallback(() => {
        setPopoverAnchor(null);
        setHits([]);
        setSelectedIndex(0);
        setFilterQuery('');
    }, []);

    const runSearch = useCallback(async (companyName: string): Promise<CrGovCompanyHit[]> => {
        const name = companyName.trim();
        if (!name) return [];
        return searchCrGovByCompanyName(name.toUpperCase());
    }, []);

    const openWithHits = useCallback((list: CrGovCompanyHit[], anchorEl: HTMLElement | null) => {
        setHits(list);
        setSelectedIndex(0);
        setFilterQuery('');
        setPopoverAnchor(anchorEl);
    }, []);

    /** toolIcon: search CR ? Popover or Snackbar */
    const lookupFromIcon = useCallback(async (anchorEl: HTMLElement | null) => {
        const form = optsRef.current.getForm();
        const name = norm(form.companyName);
        if (!name || lookingUpRef.current) return;
        lookingUpRef.current = true;
        setLookingUp(true);
        try {
            const list = await runSearch(name);
            if (!list.length) {
                optsRef.current.showSnackbar(EMPTY_MSG, 'warning');
                return;
            }
            openWithHits(list, anchorEl);
        } catch {
            optsRef.current.showSnackbar(EMPTY_MSG, 'warning');
        } finally {
            lookingUpRef.current = false;
            setLookingUp(false);
        }
    }, [runSearch, openWithHits]);

    /**
     * Validate: matched / open Popover / empty.
     */
    const validateFromExternal = useCallback(async (anchorEl: HTMLElement | null): Promise<CompanyCrValidateOutcome> => {
        const form = optsRef.current.getForm();
        const name = norm(form.companyName);
        if (!name) {
            optsRef.current.showSnackbar(EMPTY_MSG, 'warning');
            return 'empty';
        }
        if (lookingUpRef.current) return 'error';
        lookingUpRef.current = true;
        setLookingUp(true);
        try {
            const list = await runSearch(name);
            if (!list.length) {
                optsRef.current.showSnackbar(EMPTY_MSG, 'warning');
                return 'empty';
            }
            if (hasMatchingCrHit(form, list)) {
                return 'matched';
            }
            openWithHits(list, anchorEl);
            return 'opened';
        } catch {
            optsRef.current.showSnackbar(EMPTY_MSG, 'warning');
            return 'error';
        } finally {
            lookingUpRef.current = false;
            setLookingUp(false);
        }
    }, [runSearch, openWithHits]);

    /** Convert selected filtered row into form (no save API) */
    const convertSelected = useCallback(() => {
        const hit = filteredHits[selectedIndex];
        if (!hit) return;
        optsRef.current.applyHit(hit);
        closePopover();
    }, [filteredHits, selectedIndex, closePopover]);

    /** Update filter; keep selection on first visible row */
    const onFilterQueryChange = useCallback((value: string) => {
        setFilterQuery(value);
        setSelectedIndex(0);
    }, []);

    return {
        lookingUp,
        hits,
        filteredHits,
        showFilter,
        filterQuery,
        onFilterQueryChange,
        selectedIndex,
        setSelectedIndex,
        popoverAnchor,
        closePopover,
        lookupFromIcon,
        validateFromExternal,
        convertSelected,
    };
}
